import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  TextField,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  Avatar,
  Button
} from "@mui/material";

import { fetchallcitylist } from "src/utils/common";
import LocationPicker from "./LocationPicker";
import { Routes } from "../../../constant/api_url"

const defaultFormData = {
  support_title: "",
  city_id: 0,
  name: "",
  designation: "",
  mobile: "",
  email: "",
  address: "",
  business_name: "",
  apartment_building: "",
  area: "",
  city: "",
  latitude: "",
  longitude: "",
  weekday_hours_from: "10:00",
  weekday_hours_to: "18:00",
  saturday_hours_from: "10:00",
  saturday_hours_to: "14:00",
  saturday_closed: false,
  sunday_status: "Closed",
  sunday_hours_from: "10:00",
  sunday_hours_to: "14:00"
};

export default function CitySupportForm({ formdata, setFormData, errors, setErrors, isLoading, profileImageFile, setProfileImageFile }) {
  let loginUser = JSON.parse(localStorage.getItem("userData"));
  const isCityAdmin = loginUser?.u_type == 1;
  const [data, setData] = useState(formdata || defaultFormData);
  const [citylist, setcitylist] = useState([]);
  const fileInputRef = useRef(null);

  const profileImagePreview = profileImageFile
    ? URL.createObjectURL(profileImageFile)
    : (data.profile_image ? `${Routes.API_HOSTNAME}city-support/${data.profile_image}` : null);

  useEffect(() => {
    return () => {
      if (profileImageFile) URL.revokeObjectURL(profileImagePreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileImageFile]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert("File size exceeds 2MB. Please upload a smaller image.");
      return;
    }
    setProfileImageFile(file);
  };

  useEffect(() => {
    if (isCityAdmin) {
      if (formdata?.city_id != loginUser.city_id) {
        const updated = { ...formdata, city_id: loginUser.city_id };
        setData(updated);
        setFormData(updated);
      } else {
        setData(formdata || defaultFormData);
      }
    } else {
      setData(formdata || defaultFormData);
    }
  }, [formdata]);

  useEffect(() => {
    if (!isCityAdmin) {
      (async () => {
        const cities = await fetchallcitylist();
        if (cities) setcitylist(cities);
      })();
    }
  }, [isCityAdmin]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newVal = type === "checkbox" ? checked : value;
    if (name === "mobile") {
      newVal = newVal.replace(/\D/g, "").slice(0, 10);
    }
    const updated = { ...data, [name]: newVal };
    setData(updated);
    setFormData(updated);
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleTimeChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...data, [name]: value };
    setData(updated);
    setFormData(updated);
  };

  const mergeAddress = (d) => {
    const parts = [d.business_name, d.apartment_building, d.area, d.city].filter(Boolean);
    return parts.length ? parts.join(", ") : d.address;
  };

  const handleAddressFieldChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...data, [name]: value };
    updated.address = mergeAddress(updated);
    setData(updated);
    setFormData(updated);
  };

  return (
    <Grid container spacing={1.5}>
      {!isCityAdmin && data.city_id ? (
        <Grid item xs={12}>
          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
            City: {(() => {
              const c = citylist.find((c) => c.city_id == data.city_id);
              return c ? c.city_name : `City #${data.city_id}`;
            })()}
          </Typography>
        </Grid>
      ) : !isCityAdmin && !data.city_id ? (
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            No city selected — add a profile from a specific city's profile list.
          </Typography>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 180, alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Profile Photo</Typography>
            <Avatar
              src={profileImagePreview}
              sx={{ width: 100, height: 100, borderRadius: 2, bgcolor: "secondary.lighter", color: "primary.main", fontSize: 32, fontWeight: 700 }}
            >
              {data.name ? data.name.charAt(0).toUpperCase() : "?"}
            </Avatar>
            <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileSelect} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Upload Profile Photo</Typography>
            <Typography variant="caption" color="text.secondary">
              JPG, PNG or WEBP. Recommended size 400x400px.
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="outlined" onClick={() => fileInputRef.current?.click()} sx={{ textTransform: "none" }}>
                Change
              </Button>
              {(profileImageFile || data.profile_image) && (
                <Button size="small" color="error" sx={{ textTransform: "none" }} onClick={() => {
                  setProfileImageFile(null);
                  const updated = { ...data, profile_image: "" };
                  setData(updated); setFormData(updated);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}>
                  Remove
                </Button>
              )}
            </Box>
          </Box>
          <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <TextField
              fullWidth
              label="Support Title"
              name="support_title"
              value={data.support_title || ""}
              onChange={handleChange}
              error={!!errors.name}
              placeholder="e.g. City Helpline / Emergency Contact"
              required
            />
            <TextField
              label="Full Name"
              name="name"
              value={data.name || ""}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
            <TextField
              label="Designation"
              name="designation"
              value={data.designation || ""}
              onChange={handleChange}
              error={!!errors.designation}
              helperText={errors.designation}
              required
            />
            <TextField
              label="Mobile Number"
              name="mobile"
              value={data.mobile || ""}
              onChange={handleChange}
              error={!!errors.mobile}
              helperText={errors.mobile}
              required
              inputProps={{ inputMode: "numeric", pattern: "\\d{10}" }}
            />
            <TextField
              label="Email Address"
              name="email"
              type="email"
              value={data.email || ""}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Box component="span" sx={{ color: "primary.main" }}>&#9679;</Box>
          Location
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <LocationPicker
          latitude={data.latitude}
          longitude={data.longitude}
          address={data.address}
          onLocationChange={({ latitude, longitude, address, addressComponents }) => {
            const updated = {
              ...data,
              latitude,
              longitude,
              address,
              business_name: addressComponents?.business_name ?? data.business_name,
              apartment_building: addressComponents?.apartment_building ?? data.apartment_building,
              area: addressComponents?.area ?? data.area,
              city: addressComponents?.city ?? data.city
            };
            setData(updated);
            setFormData(updated);
            if (errors.address) {
              setErrors({ ...errors, address: "" });
            }
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Business Name"
          name="business_name"
          value={data.business_name || ""}
          onChange={handleAddressFieldChange}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Apartment Number & Building Name"
          name="apartment_building"
          value={data.apartment_building || ""}
          onChange={handleAddressFieldChange}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Area Name"
          name="area"
          value={data.area || ""}
          onChange={handleAddressFieldChange}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="City"
          name="city"
          value={data.city || ""}
          onChange={handleAddressFieldChange}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Box component="span" sx={{ color: "primary.main" }}>&#9679;</Box>
          Working Hours
        </Typography>
      </Grid>
      <Grid item xs={12} md={6} style={{ paddingTop: '23px' }}>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Weekdays (Mon-Fri)</Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            type="time"
            name="weekday_hours_from"
            value={data.weekday_hours_from || "10:00"}
            onChange={handleTimeChange}
            size="small"
            sx={{ flex: 1 }}
          />
          <Typography variant="body2">to</Typography>
          <TextField
            type="time"
            name="weekday_hours_to"
            value={data.weekday_hours_to || "18:00"}
            onChange={handleTimeChange}
            size="small"
            sx={{ flex: 1 }}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Saturday</Typography>
          <FormControlLabel
            control={
              <Checkbox
                name="saturday_closed"
                checked={!!data.saturday_closed}
                onChange={handleChange}
                size="small"
              />
            }
            label="Closed"
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            type="time"
            name="saturday_hours_from"
            value={data.saturday_hours_from || "10:00"}
            onChange={handleTimeChange}
            size="small"
            disabled={data.saturday_closed}
            sx={{ flex: 1 }}
          />
          <Typography variant="body2">to</Typography>
          <TextField
            type="time"
            name="saturday_hours_to"
            value={data.saturday_hours_to || "14:00"}
            onChange={handleTimeChange}
            size="small"
            disabled={data.saturday_closed}
            sx={{ flex: 1 }}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Sunday</Typography>
        <FormControl component="fieldset">
          <RadioGroup
            row
            name="sunday_status"
            value={data.sunday_status || "Closed"}
            onChange={handleChange}
          >
            <FormControlLabel value="Closed" control={<Radio size="small" />} label="Always Closed" />
            <FormControlLabel value="Custom" control={<Radio size="small" />} label="Custom Hours" />
          </RadioGroup>
        </FormControl>
        {data.sunday_status === "Custom" && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}>
            <TextField
              type="time"
              name="sunday_hours_from"
              value={data.sunday_hours_from || "10:00"}
              onChange={handleTimeChange}
              size="small"
              sx={{ flex: 1 }}
            />
            <Typography variant="body2">to</Typography>
            <TextField
              type="time"
              name="sunday_hours_to"
              value={data.sunday_hours_to || "14:00"}
              onChange={handleTimeChange}
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
